/* View menu — header dropdown for toggling panels and per-tab editor tools.
   Reuses panel-registry.js togglePanel + per-tab helpers from search.js. */

import { PANELS, togglePanel, getActivePanel, getZoneForPanel } from './panel-registry.js';
import { state } from './state.js';
import {
  getActiveTab,
  toggleActiveTabStructure,
  isStructureVisibleForActiveTab,
} from './search.js';
import { viewUsageForKindAndName } from './file-tree.js';
import { openShortcutsDialog } from './shortcuts-dialog.js';

const PANEL_GROUPS = [
  { header: 'PANELS', items: [
    { panel: 'explorer' },
    { panel: 'exec-order' },
    { panel: 'knowledge' },
    { panel: 'db-inspector' },
    { panel: 'sync-status' },
  ]},
  { sep: true, items: [
    { panel: 'editor', alwaysOn: true },
    { panel: 'diff' },
    { panel: 'pipelines' },
  ]},
  { sep: true, items: [
    { panel: 'chat' },
    { panel: 'quote-frame' },
    { panel: 'console' },
  ]},
  { header: 'EDITOR', items: [
    { editorTool: 'structure' },
    { editorTool: 'view-usage' },
    { editorTool: 'shortcuts' },
  ]},
];

let menuEl = null;

export function isViewMenuOpen() {
  return !!menuEl;
}

export function closeViewMenu() {
  if (menuEl && menuEl.parentNode) menuEl.parentNode.removeChild(menuEl);
  menuEl = null;
  document.removeEventListener('mousedown', _onDocMouseDown, true);
  document.removeEventListener('keydown', _onDocKeyDown, true);
  window.removeEventListener('layout-changed', _refreshChecks);
  window.removeEventListener('active-tab-changed', _refreshChecks);
}

function _isPanelVisible(panelId) {
  const zone = getZoneForPanel(panelId);
  if (!zone) return false;
  if (getActivePanel(zone) !== panelId) return false;
  if (zone === 'left'   && !state.leftZoneVisible)   return false;
  if (zone === 'right'  && !state.rightZoneVisible)  return false;
  if (zone === 'bottom' && !state.bottomZoneVisible) return false;
  return true;
}

function _kindFromTab(tab) {
  if (!tab) return null;
  if (tab.kind === 'global-scripts' || tab.kind === 'custom-calculations') return tab.kind;
  if (typeof tab.path === 'string') {
    if (tab.path.startsWith('global-scripts/')) return 'global-scripts';
    if (tab.path.startsWith('custom-calculations/')) return 'custom-calculations';
  }
  return null;
}

function _renderMenu() {
  const m = document.createElement('div');
  m.className = 'ws-ctx-menu view-menu';
  for (const group of PANEL_GROUPS) {
    if (group.sep) {
      const sep = document.createElement('div');
      sep.className = 'ws-ctx-sep';
      m.appendChild(sep);
    }
    if (group.header) {
      const h = document.createElement('div');
      h.className = 'view-menu-header';
      h.textContent = group.header;
      m.appendChild(h);
    }
    if (Array.isArray(group.items)) {
      for (const item of group.items) m.appendChild(_renderItem(item));
    }
  }
  return m;
}

function _renderItem(item) {
  const btn = document.createElement('button');
  btn.className = 'ws-ctx-item view-menu-item';
  btn.type = 'button';

  if (item.panel) {
    const def = PANELS[item.panel];
    if (!def) {
      btn.textContent = `(missing: ${item.panel})`;
      btn.disabled = true;
      return btn;
    }
    const visible = _isPanelVisible(item.panel);
    btn.dataset.panelId = item.panel;
    btn.innerHTML = `<span class="vm-check">${visible ? '<i class="ic ic-sm ic-check"></i>' : ''}</span><span class="vm-label">${def.label}</span>`;
    if (item.alwaysOn) {
      btn.disabled = true;
      btn.title = 'Always on';
    } else {
      btn.addEventListener('click', (e) => {
        const cmd = e.metaKey || e.ctrlKey;
        togglePanel(item.panel);
        if (cmd) closeViewMenu();
        else _refreshChecks();
      });
    }
    return btn;
  }

  if (item.editorTool === 'structure') {
    const v = isStructureVisibleForActiveTab();
    btn.dataset.editorTool = 'structure';
    const checkmark = v === true ? '<i class="ic ic-sm ic-check"></i>' : '';
    btn.innerHTML = `<span class="vm-check">${checkmark}</span><span class="vm-label">Structure outline</span>`;
    if (v === null) {
      btn.disabled = true;
      btn.title = 'No script tab active';
    } else {
      btn.addEventListener('click', (e) => {
        const cmd = e.metaKey || e.ctrlKey;
        toggleActiveTabStructure();
        if (cmd) closeViewMenu();
        else _refreshChecks();
      });
    }
    return btn;
  }

  if (item.editorTool === 'view-usage') {
    btn.dataset.editorTool = 'view-usage';
    btn.innerHTML = `<span class="vm-check"></span><span class="vm-label">View usage…</span>`;
    const tab = getActiveTab();
    const kind = _kindFromTab(tab);
    if (!kind) {
      btn.disabled = true;
      btn.title = 'No script or calculation tab active';
    } else {
      btn.addEventListener('click', () => {
        viewUsageForKindAndName(kind, tab.label.replace(/\.(py|html)$/, ''));
        closeViewMenu();
      });
    }
    return btn;
  }

  if (item.editorTool === 'shortcuts') {
    btn.dataset.editorTool = 'shortcuts';
    btn.innerHTML = `<span class="vm-check"></span><span class="vm-label">Keyboard shortcuts…</span>`;
    btn.addEventListener('click', () => {
      closeViewMenu();
      openShortcutsDialog();
    });
    return btn;
  }

  btn.textContent = '(unknown item)';
  btn.disabled = true;
  return btn;
}

function _refreshChecks() {
  if (!menuEl) return;
  for (const btn of menuEl.querySelectorAll('[data-panel-id]')) {
    const id = btn.dataset.panelId;
    const visible = _isPanelVisible(id);
    btn.querySelector('.vm-check').innerHTML = visible ? '<i class="ic ic-sm ic-check"></i>' : '';
  }
  const structBtn = menuEl.querySelector('[data-editor-tool="structure"]');
  if (structBtn) {
    const v = isStructureVisibleForActiveTab();
    structBtn.querySelector('.vm-check').innerHTML = v === true ? '<i class="ic ic-sm ic-check"></i>' : '';
    structBtn.disabled = v === null;
    structBtn.title = v === null ? 'No script tab active' : '';
  }
  const usageBtn = menuEl.querySelector('[data-editor-tool="view-usage"]');
  if (usageBtn) {
    const tab = getActiveTab();
    const supported = !!_kindFromTab(tab);
    usageBtn.disabled = !supported;
    usageBtn.title = supported ? '' : 'No script or calculation tab active';
  }
}

function _onDocMouseDown(e) {
  if (!menuEl || menuEl.contains(e.target)) return;
  const trigger = document.getElementById('btnViewMenu');
  if (trigger && trigger.contains(e.target)) return;
  closeViewMenu();
}

function _onDocKeyDown(e) {
  if (e.key === 'Escape') {
    e.preventDefault();
    closeViewMenu();
  }
}

export function openViewMenu() {
  if (menuEl) { closeViewMenu(); return; }
  const trigger = document.getElementById('btnViewMenu');
  if (!trigger) return;

  menuEl = _renderMenu();
  document.body.appendChild(menuEl);

  const r = trigger.getBoundingClientRect();
  const mr = menuEl.getBoundingClientRect();
  const left = Math.max(8, Math.min(window.innerWidth - mr.width - 8, r.right - mr.width));
  menuEl.style.left = `${left}px`;
  menuEl.style.top = `${r.bottom + 4}px`;

  document.addEventListener('mousedown', _onDocMouseDown, true);
  document.addEventListener('keydown', _onDocKeyDown, true);
  window.addEventListener('layout-changed', _refreshChecks);
  window.addEventListener('active-tab-changed', _refreshChecks);
}

export function toggleViewMenu() {
  if (menuEl) closeViewMenu();
  else openViewMenu();
}
